import { Box, Button, Stack, Toolbar, useMediaQuery } from '@mui/material'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Enquate } from '~/components/common/Enquate'
import { pagesPath } from '~/lib/$path'
import { useSetCustomProperties } from '~/lib/hooks/app/useSetCustomProperties'
import { useFeatureToggle } from '~/lib/hooks/useFeatureToggle'
import { drawerWidth, theme } from '~/lib/theme'
import { AppBottomNavigation, BOTTOM_NAVIGATION_HEIGHT } from './AppBottomNavigation'
import { AppDrawer } from './AppDrawer'
import { AppFooter } from './AppFooter'
import useAppBarHeight, { AppHeader } from './AppHeader'

const FLOATING_BUTTON_GAP = 16

export const AppLayout = (props: { children: React.ReactNode }) => {
  const router = useRouter()
  const [drawerOpen, setDrawerOpen] = useState(true)

  // techtouchカスタムプロパティのセット
  useSetCustomProperties()

  const { flag: isLineFeatureActive, flagLoading: lineFlagLoading } = useFeatureToggle('releaseLineConnect')
  // LINE連携機能が正式にリリースされるまではチャットサポートを表示する
  const isChatSupportActive = !lineFlagLoading && !isLineFeatureActive

  // デスクトップ表示時にはデフォルトで開くようにしつつ、モバイル表示時の初期描画で一瞬開くのを防止する
  // 事前レンダリングを考慮すると useMediaQuery ではフラッシュが発生してしまうのでメディアクエリで出し分ける
  // それだけだとモバイル表示でのデフォルト状態を変更できないので状態を更新するまで描画しないようにするフラグを用意する
  const [isMobileAndRender, setIsMobileAndRender] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false)
      setIsMobileAndRender(true)
    }
  }, [isMobile])

  const appBarHeight = useAppBarHeight()

  return (
    <Box sx={{ '--MuiAppBar-height': `${appBarHeight}px` }}>
      <Stack>
        <AppDrawer open={drawerOpen} isMobileAndRender={isMobileAndRender} onClose={() => setDrawerOpen(false)} />
        <Box
          sx={(theme) => ({
            ml: drawerOpen && !isMobile ? drawerWidth + 'px' : 0,
            transition: theme.transitions.create('margin', {
              duration: theme.transitions.duration.leavingScreen,
            }),
          })}
        >
          <AppHeader drawerOpen={drawerOpen} onDrawerOpen={() => setDrawerOpen(true)} />
          <Enquate
            sx={{
              ml: drawerOpen && !isMobile ? drawerWidth + 'px' : 0,
              transition: theme.transitions.create('margin', {
                duration: theme.transitions.duration.leavingScreen,
              }),
            }}
          />
          <Box
            sx={{
              minHeight: isMobile ? undefined : '100vh',
            }}
            component="main"
          >
            {/* AppBarの高さ分 */}
            <Toolbar />
            {props.children}
          </Box>

          <AppFooter />

          {/* ボタン押下時の挙動はテックタッチで作成するため、コード上では挙動を記載しない */}
          {router.pathname === pagesPath.cancellations.subscription.$url().pathname && (
            <Button
              variant="contained"
              sx={{
                ...cancelBtnStyle,
                bottom: BOTTOM_NAVIGATION_HEIGHT + 48 + FLOATING_BUTTON_GAP + 30,
                marginBottom: 'env(safe-area-inset-bottom)',
                [theme.breakpoints.down('md')]: {
                  bottom: BOTTOM_NAVIGATION_HEIGHT + 48 + FLOATING_BUTTON_GAP + 50, // Tayoriチャットボットの上に配置
                },
              }}
            >
              ご解約手続きはこちら
            </Button>
          )}

          {isMobile && <AppBottomNavigation />}
        </Box>
      </Stack>
    </Box>
  )
}

const cancelBtnStyle = {
  position: 'fixed',
  right: '20px',
  borderRadius: '50px',
  padding: '13.5px 20px',
  boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.3)',
  fontWeight: 'bold',
}
